"use client";

import * as posthog from "@lib/posthog";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useInternalUser } from "@/lib/auth";
import { FEATURE_FLAGS, type FeatureFlag } from "./constants";

export function useFeatureFlag(featureFlag: FeatureFlag): boolean | undefined {
  return useFeatureFlagEnabled(featureFlag);
}

/**
 * Gates email domain management.
 *
 * Resolution order:
 *   1. `?ff-helios-email=1` URL override — for QA / agent-browser previews.
 *      Set to `0` to force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-email`.
 */
export function useHeliosEmailEnabled(): boolean {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosEmail);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-email");
    if (override === "1" || override === "true") return true;
    if (override === "0" || override === "false") return false;
  }

  return value === true;
}

/**
 * Gates email template creation and editing.
 *
 * Resolution order:
 *   1. `?ff-helios-email=1` URL override — for QA / agent-browser previews.
 *      Set to `0` to force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-email`.
 */
export function useHeliosEmailFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosEmail);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-email");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the texting QR code generator.
 *
 * Resolution order:
 *   1. `?ff-helios-qr-codes=1` URL override — for QA / agent-browser
 *      previews while the PostHog flag is still rolling out. Set to `0`
 *      to force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-qr-codes`.
 */
export function useHeliosQrCodeFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosQrCode);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-qr-codes");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Boolean helper for the new chip-based segment editor.
 *
 * Resolution order:
 *   1. `?ff-helios-segments-new=1` URL override — for QA / agent-browser
 *      previews while the PostHog flag is still gradually rolling out.
 *      Set to `0` to force-disable; any other value falls through to
 *      PostHog.
 *   2. PostHog flag `helios-segments-new`.
 *
 * Returns false during the initial PostHog hydration window so the
 * legacy editor stays visible until PostHog confirms the user is opted in.
 */
export function useHeliosSegmentsNewEnabled(): boolean {
  const posthogValue = useFeatureFlag(FEATURE_FLAGS.heliosSegmentsNew);
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("ff-helios-segments-new");
    if (override === "1" || override === "true") return true;
    if (override === "0" || override === "false") return false;
  }
  return posthogValue === true;
}

/**
 * Boolean helper for the new stepped DateTimePicker on blast scheduling.
 *
 * Resolution order:
 *   1. `?ff-helios-datetime-field-new=1` URL override — for QA / agent-browser
 *      previews while the PostHog flag is still rolling out. Set to `0` to
 *      force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-datetime-field-new`.
 *
 * Returns false during the initial PostHog hydration window so the legacy
 * datetime field stays visible until PostHog confirms the user is opted in.
 */
export function useHeliosDatetimeFieldNewEnabled(): boolean {
  const posthogValue = useFeatureFlag(FEATURE_FLAGS.heliosDatetimeFieldNew);
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const override = params.get("ff-helios-datetime-field-new");
    if (override === "1" || override === "true") return true;
    if (override === "0" || override === "false") return false;
  }
  return posthogValue === true;
}

/**
 * Gates browser-based voice calling (Twilio Voice JS SDK).
 *
 * Resolution order:
 *   1. `?ff-helios-web-calling=1` URL override — for QA / agent-browser
 *      previews while the PostHog flag is still rolling out. Set to `0`
 *      to force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-web-calling`.
 */
export function useHeliosWebCallingFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosWebCalling);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-web-calling");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the Tasks feature.
 *
 * Resolution order:
 *   1. `?ff-helios-tasks=1` URL override — for QA / agent-browser previews
 *      while the PostHog flag is still rolling out. Set to `0` to
 *      force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-tasks`.
 */
export function useHeliosTasksFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosTasks);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-tasks");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the Helios Assistant.
 *
 * Resolution order:
 *   1. `?ff-helios-assistant=1` URL override — for QA / agent-browser
 *      previews while the PostHog flag is still rolling out. Set to `0`
 *      to force-disable; any other value falls through to PostHog.
 *   2. PostHog flag `helios-assistant`.
 */
export function useHeliosAssistantFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosAssistant);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-assistant");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the gym-level Web SDK installation page.
 *
 * `?ff-helios-web-sdk=1` enables the page for QA and demo screenshots before
 * the PostHog rollout reaches the current user.
 */
export function useHeliosWebSdkFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosWebSdk);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-web-sdk");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the Pages surface (combined forms + landing pages list and the
 * landing page builder).
 *
 * `?ff-helios-pages=1` enables the surface for QA and demo screenshots
 * before the PostHog rollout reaches the current user. The surface spans
 * several routes (/pages, the landing page builder), so the override
 * sticks for the rest of the tab once set; `?ff-helios-pages=0` clears it
 * and force-disables.
 */
export function useHeliosPagesFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosPages);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-pages");
    try {
      if (override === "1" || override === "true") {
        window.sessionStorage.setItem("ff-helios-pages", "1");
      } else if (override === "0" || override === "false") {
        window.sessionStorage.removeItem("ff-helios-pages");
        return { enabled: false, isLoading: false };
      }
      if (window.sessionStorage.getItem("ff-helios-pages") === "1") {
        return { enabled: true, isLoading: false };
      }
    } catch {
      // Storage can be unavailable (private browsing); the URL param still
      // works for the current route.
      if (override === "1" || override === "true") return { enabled: true, isLoading: false };
      if (override === "0" || override === "false") return { enabled: false, isLoading: false };
    }
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Reads a `?ff-<key>` override that sticks for the rest of the tab.
 *
 * `=1` stores the override in sessionStorage so it survives navigation
 * between the feature's routes; `=0` clears it and force-disables. Only
 * Helios staff can force a feature on: for everyone else `=1` is ignored
 * and any stored override is dropped, so a shared link cannot expose a
 * surface PostHog has not enabled for that gym.
 */
function readStickyOverride(key: string, canForceOn: boolean): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  const override = new URLSearchParams(window.location.search).get(key);
  const forceOn = override === "1" || override === "true";
  const forceOff = override === "0" || override === "false";
  try {
    if (forceOff || !canForceOn) {
      window.sessionStorage.removeItem(key);
    } else if (forceOn) {
      window.sessionStorage.setItem(key, "1");
    }
    if (forceOff) return false;
    if (canForceOn && window.sessionStorage.getItem(key) === "1") return true;
  } catch {
    // Storage can be unavailable (private browsing); the URL param still
    // works for the current route.
    if (forceOff) return false;
    if (forceOn && canForceOn) return true;
  }
  return undefined;
}

/**
 * Gates the Pulse feed.
 *
 * `?ff-helios-pulse=1` enables the surface for QA and demo screenshots
 * before the PostHog rollout reaches the current user. Pulse spans several
 * routes (/pulse, item detail, review), so the override sticks for the rest
 * of the tab once set; `?ff-helios-pulse=0` clears it and force-disables.
 * Pulse approves drafts that message customers, so only Helios staff can
 * force it on.
 */
export function useHeliosPulseFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosPulse);
  const internal = useInternalUser();

  const override = readStickyOverride("ff-helios-pulse", internal);
  if (override !== undefined) return { enabled: override, isLoading: false };

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates Stages and the playbook UI on the Flows and Lists indexes.
 *
 * `?ff-helios-stages=1` enables the surface for QA and demo screenshots
 * before the PostHog rollout reaches the current user. Stages spans several
 * routes (/stages, /flows, /lists), so the override sticks for the rest of
 * the tab once set; `?ff-helios-stages=0` clears it and force-disables.
 * Only Helios staff can force it on.
 */
export function useHeliosStagesFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosStages);
  const internal = useInternalUser();

  const override = readStickyOverride("ff-helios-stages", internal);
  if (override !== undefined) return { enabled: override, isLoading: false };

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the key insight card in the contact sidebar.
 *
 * `?ff-helios-key-insight=1` enables the card for QA and demo screenshots
 * before the PostHog rollout reaches the current user; `0` force-disables.
 * Returns false during the initial PostHog hydration window so the sidebar
 * renders without the card until PostHog confirms the user is opted in.
 */
export function useHeliosKeyInsightFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosKeyInsight);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-key-insight");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}

/**
 * Gates the Brand kit settings page and import wizard.
 *
 * `?ff-helios-brand-kit=1` enables the page for QA and demo screenshots before
 * the PostHog rollout reaches the current user.
 */
export function useHeliosBrandKitFeature(): { enabled: boolean; isLoading: boolean } {
  const value = useFeatureFlag(FEATURE_FLAGS.heliosBrandKit);

  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("ff-helios-brand-kit");
    if (override === "1" || override === "true") return { enabled: true, isLoading: false };
    if (override === "0" || override === "false") return { enabled: false, isLoading: false };
  }

  return {
    enabled: value === true,
    isLoading: posthog.isConfigured && value === undefined,
  };
}
